/*
 * Every date, number and list goes through Intl with an explicit locale and an explicit time zone —
 * never `toLocaleString()`, which would silently follow the visitor's browser while the interface
 * speaks one language (Q38; the eslint rule `no-restricted-syntax` enforces it).
 *
 * That language is English, and the locale is `en-GB` rather than `en-US`: the team reads
 * day-before-month ("6 September 2026") and a 24-hour clock ("23:30"), which is what en-GB gives
 * and en-US does not ("September 6, 2026", "11:30 PM"). The clock is pinned with an explicit
 * `hour12: false` so a later CLDR release cannot quietly move the interface to AM/PM.
 *
 * The zone is not the browser's either: an account carries its own (`account.timezone`, default
 * Europe/Berlin — schema.md §2.1), so the shell calls `setTimeZone` once the bootstrap has said
 * which one, and every stamp in the interface moves together. The formatters are rebuilt at that
 * moment and never again: an Intl constructor is expensive and a message list calls these per row.
 *
 * Two functions here are not pure formatters: `formatDay` names today and yesterday in words and
 * `formatRelative` names the last minute "just now", so they read three entries of core's catalogue.
 * They read them out of `en.ts` rather than through `t()`, because `t()` lives in `t.ts`, which reads
 * `LOCALE` from this file while it is being evaluated — importing it back would be a cycle that
 * throws at load time. None of the three carries a placeholder, so the catalogue entry *is* the text.
 */

import { en } from "./en.ts"

export const LOCALE = "en-GB"
export const DEFAULT_TIME_ZONE = "Europe/Berlin"

function build(timeZone: string) {
	return {
		time: new Intl.DateTimeFormat(LOCALE, {
			hour: "2-digit",
			minute: "2-digit",
			hour12: false,
			timeZone,
		}),
		date: new Intl.DateTimeFormat(LOCALE, {
			day: "numeric",
			month: "long",
			year: "numeric",
			timeZone,
		}),
		weekday: new Intl.DateTimeFormat(LOCALE, {
			weekday: "long",
			day: "numeric",
			month: "long",
			timeZone,
		}),
		// "3 September" — a day of this year, where the year would say nothing.
		dayMonth: new Intl.DateTimeFormat(LOCALE, { day: "numeric", month: "long", timeZone }),
		// "September 2026" — a heading over a month of things, where no single day is meant.
		month: new Intl.DateTimeFormat(LOCALE, { month: "long", year: "numeric", timeZone }),
		// "2026" — only ever compared, never shown: whether two moments are in one year.
		year: new Intl.DateTimeFormat(LOCALE, { year: "numeric", timeZone }),
		// Every part of a moment in the account's zone. It is never shown: it is how far the zone
		// stands from UTC at that moment, which is the one thing calendar arithmetic needs and the
		// only thing in the browser that knows when a zone last moved its clocks. `hourCycle: "h23"`
		// rather than `hour12: false`, because that spelling renders midnight as "24" in some ICU
		// releases and an hour of 24 would carry every sum into the following day.
		zoned: new Intl.DateTimeFormat(LOCALE, {
			year: "numeric",
			month: "2-digit",
			day: "2-digit",
			hour: "2-digit",
			minute: "2-digit",
			second: "2-digit",
			hourCycle: "h23",
			timeZone,
		}),
		dateTime: new Intl.DateTimeFormat(LOCALE, {
			dateStyle: "medium",
			timeStyle: "short",
			hour12: false,
			timeZone,
		}),
	}
}

let zone = DEFAULT_TIME_ZONE
let formats = build(zone)

/** The zone every stamp is rendered in. */
export function timeZone(): string {
	return zone
}

/** Switches every date formatter to the signed-in account's zone; an unknown zone is ignored. */
export function setTimeZone(next: string): void {
	if (next === zone) return
	try {
		formats = build(next)
		zone = next
	} catch {
		// An IANA name this engine does not know would break every stamp; keep the previous zone.
	}
}

const relative = new Intl.RelativeTimeFormat(LOCALE, { numeric: "auto" })
const decimal = new Intl.NumberFormat(LOCALE)
const money = new Intl.NumberFormat(LOCALE, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
const list = new Intl.ListFormat(LOCALE, { style: "long", type: "conjunction" })

/** "14:32" — the stamp beside a message. */
export function formatTime(ms: number): string {
	return formats.time.format(ms)
}

/**
 * "14:32" in somebody else's zone — the local time on a profile, which is the one stamp in the
 * interface that is deliberately *not* the reader's own. A zone this engine does not know answers
 * the empty string rather than throwing, so an account carrying a stale IANA name loses the row and
 * nothing else.
 */
export function formatTimeIn(ms: number, zone: string): string {
	try {
		return new Intl.DateTimeFormat(LOCALE, {
			hour: "2-digit",
			minute: "2-digit",
			hour12: false,
			timeZone: zone,
		}).format(ms)
	} catch {
		return ""
	}
}

/** "6 September 2026" — a day divider outside the current week. */
export function formatDate(ms: number): string {
	return formats.date.format(ms)
}

/**
 * "September 2026" — the month a moment falls in, in the account's zone. It is the heading over a
 * list that has grown past the point where a day means anything: the files of a conversation, once
 * there are more of them than a reader scrolls past (VISUAL.md §7.7). The year is always there,
 * because a list that goes back far enough reaches last September too.
 */
export function formatMonth(ms: number): string {
	return formats.month.format(ms)
}

const DAY_MS = 86_400_000

/** How far the account's zone stands from UTC at one moment, in milliseconds. */
function zoneOffset(ms: number): number {
	const parts = formats.zoned.formatToParts(ms)
	const part = (type: Intl.DateTimeFormatPartTypes) =>
		Number(parts.find((candidate) => candidate.type === type)?.value ?? 0)
	const asUtc = Date.UTC(
		part("year"),
		part("month") - 1,
		part("day"),
		part("hour"),
		part("minute"),
		part("second"),
	)
	return asUtc - Math.floor(ms / 1000) * 1000
}

/**
 * A moment as its own wall clock: the number whose *UTC* parts are the hour a person in the
 * account's zone reads off the wall. Which day a moment falls on and where that day ends are
 * arithmetic on this number, carried back to a real moment with the offset it was made from.
 */
function wallClock(ms: number): number {
	return ms + zoneOffset(ms)
}

/**
 * Which calendar day a moment falls on in the account's zone, counted in days since the epoch. It is
 * the comparison `formatDay` needs and the one a millisecond distance cannot give: two moments
 * eleven hours apart are "today" and "yesterday" when midnight stands between them, and the same
 * eleven hours are one day when it does not.
 */
function dayNumber(ms: number): number {
	return Math.floor(wallClock(ms) / DAY_MS)
}

/** Whether two moments fall on one calendar day in the account's zone. */
export function sameDay(a: number, b: number): boolean {
	return dayNumber(a) === dayNumber(b)
}

/**
 * The last millisecond of the calendar day a moment falls on, in the account's zone — what a status
 * that clears "today" is given. The offset is read twice because the day being ended need not end in
 * the offset it began in: on the two nights a year a day is 23 or 25 hours long, one reading would
 * answer an hour out, which is an hour on the wrong side of midnight.
 */
export function endOfDay(ms: number): number {
	const opened = zoneOffset(ms)
	const endOnTheWall = Math.floor((ms + opened) / DAY_MS) * DAY_MS + DAY_MS - 1
	return endOnTheWall - zoneOffset(endOnTheWall - opened)
}

/**
 * The last millisecond of the week a moment falls in, in the account's zone. The interface's locale
 * is en-GB, whose week runs Monday to Sunday, so the week ends when Sunday does — and asked on a
 * Sunday it is the end of that day, which is the honest answer rather than a week away.
 */
export function endOfWeek(ms: number): number {
	// 1970-01-01 was a Thursday, so day 0 is weekday 3 where Monday is 0.
	const weekday = (dayNumber(ms) + 3) % 7
	const opened = zoneOffset(ms)
	// Noon of the week's last day on the wall, which is far enough from both midnights that an hour
	// of daylight saving in between cannot move the answer into a neighbouring day.
	const noonOnTheWall =
		Math.floor((ms + opened) / DAY_MS) * DAY_MS + (6 - weekday) * DAY_MS + DAY_MS / 2
	return endOfDay(noonOnTheWall - opened)
}

/**
 * The one name a day has in this product (D-10 P2): "Today", "Yesterday", the weekday with its date
 * inside the last week ("Sunday 6 September"), the day and month inside this year ("3 September"),
 * and the full date before that ("3 September 2025"). The timeline's day pill, the jump panel's
 * presets, Activity's headings and a search hit all call it, so a date is never spelled two ways in
 * one interface.
 *
 * `now` is passed in rather than read from the clock: the caller holds the interface's one clock
 * (`useNow`), which is what makes a timeline left open overnight say "Yesterday" the next morning.
 */
export function formatDay(ms: number, now: number): string {
	const days = dayNumber(now) - dayNumber(ms)
	if (days === 0) return en["day.today"]
	if (days === 1) return en["day.yesterday"]
	// Only backwards: a stamp in the future — a clock that disagrees with the server's — is a date,
	// not "Thursday".
	if (days > 1 && days < 7) return formats.weekday.format(ms)
	if (formats.year.format(ms) === formats.year.format(now)) return formats.dayMonth.format(ms)
	return formats.date.format(ms)
}

/**
 * A day and a time, for a sentence that points at a moment: "since 09:12" for today, "since
 * Yesterday 17:40" and "since 3 September 17:40" otherwise. Today's name is dropped because nobody
 * writes "since today 09:12" — the clock alone is what a person says about the day they are in.
 */
export function formatDayTime(ms: number, now: number): string {
	const day = formatDay(ms, now)
	if (day === en["day.today"]) return formats.time.format(ms)
	return `${day} ${formats.time.format(ms)}`
}

/** "6 Sept 2026, 14:32" — a tooltip, and the visible text of a <time> element. */
export function formatDateTime(ms: number): string {
	return formats.dateTime.format(ms)
}

/** The `datetime` attribute of a <time> element: ISO 8601, never localised. */
export function isoDateTime(ms: number): string {
	return new Date(ms).toISOString()
}

/** Below this a distance is not counted at all; `formatRelative` says "just now" instead. */
const MINUTE_MS = 60_000

/** The units a distance is counted in, smallest first. There is no second: see `formatRelative`. */
const UNITS: [Intl.RelativeTimeFormatUnit, number][] = [
	["minute", MINUTE_MS],
	["hour", 3_600_000],
	["day", 86_400_000],
	["week", 604_800_000],
	["month", 2_629_800_000],
	["year", 31_557_600_000],
]

/**
 * "3 minutes ago", "yesterday" — the largest unit that fits the distance.
 *
 * Under a minute it is "just now" and not a number of seconds. Nobody reads "47 seconds ago" as
 * anything but "a moment ago", the stamp is stale before it is read, and rounding produced the
 * sentence that gave it away: half a minute and a half is "60 seconds ago" standing in a column
 * beside "1 minute ago" (X-B1 P7). A moment in the near future is the same word — a clock that
 * disagrees with the server's by a few seconds must not say "in 8 seconds".
 */
export function formatRelative(ms: number, now: number = Date.now()): string {
	const delta = ms - now
	const abs = Math.abs(delta)
	if (abs < MINUTE_MS) return en["day.justNow"]
	let chosen = UNITS[0]!
	for (const unit of UNITS) if (abs >= unit[1]) chosen = unit
	return relative.format(Math.round(delta / chosen[1]), chosen[0])
}

export function formatNumber(value: number): string {
	return decimal.format(value)
}

/**
 * An amount of money with its two decimals, in the interface's own locale. It takes no currency
 * symbol: the one figure this product prints is a model call's cost in US dollars, and the screen
 * that prints it names the currency in its own sentence, so the number is a number here.
 */
export function formatAmount(value: number): string {
	return money.format(value)
}

/**
 * "999+" — a count that has run past what its badge can show. `cap` is the largest number the badge
 * still prints exactly; at it and above it the badge prints `cap+`, because that is all the count
 * means by then. The caller names the ceiling and there is no default: the only one the product has
 * is the wire's own `CountCap`, where the server stops counting, and a second hand-written copy of
 * it here could only ever disagree with the server. A four-digit number stretches a sidebar row and
 * a rail badge past the width they are drawn at, and nobody triages by the exact number once it is
 * that large: 999 waiting messages and 4000 waiting messages are the same message.
 */
export function formatCapped(value: number, cap: number): string {
	if (value >= cap) return `${formatNumber(cap)}+`
	return formatNumber(value)
}

/** "Anna, Bert and Chris" — reaction tooltips and member lines. */
export function formatList(items: string[]): string {
	return list.format(items)
}

/**
 * "43 %" — a share of something, from a fraction between 0 and 1. Whole percent by default: a meter
 * that says "43.7 %" claims a precision nobody reads it for. A share that is above zero but rounds
 * to nothing says "< 1 %", because "0 %" beside a bar that has begun is a contradiction.
 */
export function formatPercent(fraction: number, digits = 0): string {
	const shown = new Intl.NumberFormat(LOCALE, {
		style: "percent",
		maximumFractionDigits: digits,
	})
	const floor = 10 ** -digits / 100
	if (fraction > 0 && fraction < floor) return `< ${shown.format(floor)}`
	return shown.format(fraction)
}

/**
 * "34k", "1.6 M" — a count of tokens or lines where the magnitude is the point: the context meter
 * ("86k of 200k tokens"), a workflow's spend ("1.6 M tokens so far"). One decimal below ten of a
 * unit and none above it, and a value that rounds up into the next unit is written in that unit
 * ("1 M", never "1,000k"). The thousand is a lower-case "k" against the digits and the million a
 * spaced "M", which is how the team writes both; below a thousand the number is exact.
 */
export function formatCompact(value: number): string {
	const sign = value < 0 ? "-" : ""
	const abs = Math.abs(value)
	if (abs < 1_000) return sign + formatNumber(Math.round(abs))
	const inUnit = (n: number) =>
		new Intl.NumberFormat(LOCALE, { maximumFractionDigits: n < 10 ? 1 : 0 }).format(n)
	const rounded = (n: number) => (n < 10 ? Math.round(n * 10) / 10 : Math.round(n))
	const thousands = rounded(abs / 1_000)
	if (thousands < 1_000) return `${sign}${inUnit(thousands)}k`
	return `${sign}${inUnit(rounded(abs / 1_000_000))} M`
}

/** The English ordinal suffix for each plural category `Intl.PluralRules` answers ("2nd"). */
const ORDINAL_SUFFIX: Record<Intl.LDMLPluralRule, string> = {
	zero: "th",
	one: "st",
	two: "nd",
	few: "rd",
	many: "th",
	other: "th",
}
const ordinal = new Intl.PluralRules(LOCALE, { type: "ordinal" })

/** "2nd", "11th", "23rd" — a place in a line ("2nd in line for the dev machine"). */
export function formatOrdinal(value: number): string {
	return `${formatNumber(value)}${ORDINAL_SUFFIX[ordinal.select(value)]}`
}

/** The units a duration is spelled in, largest first. */
const DURATION_UNITS: [Intl.NumberFormatOptions["unit"] & string, number][] = [
	["day", 86_400_000],
	["hour", 3_600_000],
	["minute", 60_000],
	["second", 1_000],
]

const durationFormats = new Map<string, Intl.NumberFormat>()
function durationUnit(unit: string, style: "short" | "long"): Intl.NumberFormat {
	const key = `${unit}:${style}`
	let format = durationFormats.get(key)
	if (format === undefined) {
		format = new Intl.NumberFormat(LOCALE, { style: "unit", unit, unitDisplay: style })
		durationFormats.set(key, format)
	}
	return format
}

/**
 * How long something has taken or will take: "45 secs", "4 mins", "1 hr 20 mins", "2 days 3 hrs" —
 * or, in a sentence, "8 hours", "30 minutes". At most the two largest units that are not zero, and
 * seconds only under a minute, counted down
 * (a turn four minutes and fifty seconds old has run "4 mins", never "5"), because a clock beside
 * running work that rounds up claims time that has not passed. Under a second is "0 secs": a
 * duration is never negative and never blank. The words are CLDR's en-GB units, so "mins" and
 * "hrs" are what the interface's own locale calls them.
 */
export function formatDuration(ms: number, style: "short" | "long" = "short"): string {
	let rest = Math.max(0, Math.floor(ms / 1_000) * 1_000)
	const parts: string[] = []
	for (const [unit, size] of DURATION_UNITS) {
		// Seconds are spelled only while nothing larger is: a clock beside running work that ticks
		// every second past the first minute is a distraction, not information.
		if (unit === "second" && parts.length > 0) break
		const count = Math.floor(rest / size)
		if (count > 0) {
			parts.push(durationUnit(unit, style).format(count))
			rest -= count * size
		} else if (parts.length > 0) {
			// A zero between two units ends the phrase: "2 days", not "2 days 0 hrs 5 mins".
			break
		}
		if (parts.length === 2) break
	}
	return parts.length === 0 ? durationUnit("second", style).format(0) : parts.join(" ")
}

const BYTE_UNITS = ["B", "kB", "MB", "GB", "TB"] as const
const BINARY_UNITS = ["B", "KiB", "MiB", "GiB", "TiB"] as const

/** "2.4 MB" — attachment sizes, and every file's, in the decimal units file managers show. */
export function formatBytes(bytes: number): string {
	return scaledBytes(bytes, 1000, BYTE_UNITS)
}

/**
 * "50 GiB" — the dev machine's sizes, in the binary units they are configured and documented in: a
 * container's quota, the pool, the budget for what deactivated chats keep, memory, and a disk used
 * measured against its quota. Written in `formatBytes`' units, the 50 GiB quota a person was told
 * would read "53.7 GB" and look like a different number.
 */
export function formatBinaryBytes(bytes: number): string {
	return scaledBytes(bytes, 1024, BINARY_UNITS)
}

/** One decimal below a hundred of a unit and none from there, so a whole size reads as set. */
function scaledBytes(bytes: number, step: number, units: readonly string[]): string {
	let value = bytes
	let unit = 0
	while (value >= step && unit < units.length - 1) {
		value /= step
		unit++
	}
	const digits = unit === 0 || value >= 100 ? 0 : 1
	return `${new Intl.NumberFormat(LOCALE, { maximumFractionDigits: digits }).format(value)} ${units[unit]!}`
}
